///
/// EarthBrowser
///
/// Created by Matt Giger on 9/17/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Validation for the persisted placemarks slice. Malformed member
/// locations are dropped; a placemark with no valid locations is dropped
/// entirely (a placemark is its locations — without them it is useless).
///

import type { Placemark, PlacemarkLocation, PlacemarksState } from "../placemarksSlice"
import { cDefaultPlacemarkColor, cMaxPlacemarkLocations, cPlacemarkColors } from "../placemarksSlice"

const cMaxNameLength = 200
const cMaxNoteLength = 4096
const cMaxMemberNoteLength = 1024
const cMaxTags = 20
const cMaxTagLength = 64

function isFiniteNumber(value: unknown): value is number {
	return typeof value === "number" && isFinite(value)
}

function isISODateString(value: unknown): value is string {
	return typeof value === "string" && !isNaN(Date.parse(value))
}

function validateLocation(location: unknown): PlacemarkLocation | null {
	if (!location || typeof location !== "object") return null
	const l = location as Partial<PlacemarkLocation>

	if (typeof l.id !== "string" || l.id.length === 0) return null
	if (!isFiniteNumber(l.lat) || l.lat < -90 || l.lat > 90) return null
	if (!isFiniteNumber(l.lon) || l.lon < -180 || l.lon > 180) return null

	return {
		id: l.id,
		lat: l.lat,
		lon: l.lon,
		name: typeof l.name === "string" && l.name.trim().length > 0
			? l.name.trim().slice(0, cMaxNameLength) : undefined,
		altitudeM: isFiniteNumber(l.altitudeM) ? l.altitudeM : undefined,
		note: typeof l.note === "string" && l.note.trim().length > 0
			? l.note.trim().slice(0, cMaxMemberNoteLength) : undefined,
	}
}

/**
 * Validate a single placemark. Returns the (possibly repaired) placemark,
 * or null when it is malformed beyond repair and should be dropped.
 */
export function validatePlacemark(placemark: unknown): Placemark | null {
	if (!placemark || typeof placemark !== "object") return null
	const p = placemark as Partial<Placemark>

	if (typeof p.id !== "string" || p.id.length === 0) return null
	if (typeof p.name !== "string" || p.name.trim().length === 0) return null
	if (!Array.isArray(p.locations)) return null

	const locations: PlacemarkLocation[] = []
	const seenIds = new Set<string>()
	for (const raw of p.locations) {
		if (locations.length >= cMaxPlacemarkLocations) break
		const location = validateLocation(raw)
		if (!location || seenIds.has(location.id)) continue
		locations.push(location)
		seenIds.add(location.id)
	}
	if (locations.length === 0) return null

	const createdAt = isISODateString(p.createdAt) ? p.createdAt : new Date().toISOString()
	const updatedAt = isISODateString(p.updatedAt) ? p.updatedAt : createdAt

	return {
		id: p.id,
		name: p.name.trim().slice(0, cMaxNameLength),
		locations,
		anchorId: typeof p.anchorId === "string" && seenIds.has(p.anchorId) ? p.anchorId : undefined,
		color: typeof p.color === "string" && cPlacemarkColors[p.color] ? p.color : cDefaultPlacemarkColor,
		note: typeof p.note === "string" && p.note.length > 0 ? p.note.slice(0, cMaxNoteLength) : undefined,
		tags: Array.isArray(p.tags)
			? p.tags
				.filter((tag): tag is string => typeof tag === "string" && tag.length > 0)
				.map(tag => tag.slice(0, cMaxTagLength))
				.slice(0, cMaxTags)
			: [],
		createdAt,
		updatedAt,
	}
}

/**
 * Validate the whole slice, dropping malformed placemarks and tombstones.
 */
export function validatePlacemarksState(state: unknown): PlacemarksState {
	const fallback: PlacemarksState = { placemarks: {}, deleted: {}, lastSyncedAt: null }
	if (!state || typeof state !== "object") return fallback
	const s = state as Partial<PlacemarksState>

	const placemarks: Record<string, Placemark> = {}
	if (s.placemarks && typeof s.placemarks === "object") {
		for (const [id, placemark] of Object.entries(s.placemarks)) {
			const validated = validatePlacemark(placemark)
			if (validated?.id === id) {
				placemarks[id] = validated
			}
		}
	}

	const deleted: Record<string, string> = {}
	if (s.deleted && typeof s.deleted === "object") {
		for (const [id, deletedAt] of Object.entries(s.deleted)) {
			if (typeof id === "string" && id.length > 0 && isISODateString(deletedAt)) {
				deleted[id] = deletedAt
			}
		}
	}

	return {
		placemarks,
		deleted,
		lastSyncedAt: isISODateString(s.lastSyncedAt) ? s.lastSyncedAt : null,
	}
}
